import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

/** @note Mutation hook to handle registration request */
export const useRegister = () => {
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const registerMutation = useMutation({
        mutationFn: async (values) => {
            const host = window.location.host.startsWith('127.0.0.1')
                ? 'teamsdev.tcap.dev'
                : window.location.host;

            const formData = new FormData();
            formData.append('registrationFormDomain', host);
            formData.append('o365Sync', true);
            formData.append('trunkLocationID', 'MEL');

            // Map v5 form values to API expected format
            formData.append('companyName', values.companyName);
            formData.append('entityName', values.companyName);
            formData.append('firstName', values.firstName);
            formData.append('lastName', values.lastName);
            formData.append('emailAddress', values.email);
            formData.append('password', values.password);
            formData.append('contactNumber', values.contactNumber || '');
            formData.append('partnerCode', values.partnerCode || '');
            formData.append('approvalCode', values.approvalCode || '');
            formData.append(
                'g-recaptcha-response',
                values.recaptchaToken || '',
            );

            const response = await fetch(
                `${import.meta.env.VITE_APP_API_URL}/register`,
                {
                    headers: {
                        'x-requestscope': '20',
                    },
                    body: formData,
                    method: 'POST',
                    mode: 'cors',
                },
            );

            const data = await response.json();

            if (data.message && !data.success) {
                throw new Error(data.message);
            }

            if (!data.success) {
                throw new Error('Registration failed. Please try again.');
            }

            return { success: true, email: values.email };
        },
        onSuccess: () => {
            setError(null);
            setSuccess(true);
            toast.success(
                'Registration successful! Please check your email for activation instructions.',
            );
        },
        onError: (error) => {
            setSuccess(false);
            setError(error.message);
            toast.error(error.message);
        },
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            await registerMutation.mutateAsync(values);
        } catch (error) {
            // Error handled in onError callback
            // Reset reCAPTCHA if available
            if (window.grecaptcha) {
                window.grecaptcha.reset();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const resetMessages = () => {
        setError(null);
        setSuccess(false);
    };

    return {
        handleSubmit,
        isLoading: registerMutation.isPending,
        error,
        success,
        resetMessages,
        mutation: registerMutation,
    };
};
